import { useEffect } from 'react';
import { StudioContext } from '../contexts/studio.context';
import { useSelector } from '@xstate/react';
import { rotateTo, Side } from '@ad3d/camera';

export function useSuperSplatBridge(editor: any) {
    const actor = StudioContext.useActorRef();
    const config = StudioContext.useSelector((state) => state.context.config);
    const cameraActor = useSelector(actor, (state) => state.children.camera);
    const splatActor = useSelector(actor, (state) => state.children.splat);

    // Sync: Camera Machine -> SuperSplat
    useEffect(() => {
        if (!editor || !cameraActor) return;

        const subscription = cameraActor.subscribe((snapshot) => {
            const view = snapshot.context.view;
            console.log('[Bridge] Camera View Changed:', view);
            
            if (!editor.scene || !editor.scene.camera) return;
            const camera = editor.scene.camera;

            if (view === '3d') {
                // Default perspective
                camera.setAzimElev(45, 45);
            } else {
                // Use shared utility for orthogonal views
                // Map CameraView ('top' | 'left' | 'right' | 'back') to Side
                const side = view as Side; 
                const { azimuth, polar } = rotateTo(side);
                
                // Convert radians to degrees for SuperSplat's setAzimElev (assuming it takes degrees based on previous code)
                // Wait, rotateTo returns radians. setAzimElev usually takes degrees in PlayCanvas apps unless specified.
                // Let's check SuperSplat's camera.ts implementation or assume degrees for now based on previous code (0, 90).
                // 90 degrees = PI/2 radians.
                
                const toDeg = (rad: number) => rad * (180 / Math.PI);
                camera.setAzimElev(toDeg(azimuth), toDeg(polar));
            }
        });

        return () => subscription.unsubscribe();
    }, [editor, cameraActor]);

    // Sync: State Machine -> SuperSplat
    // Note: Loading is handled by loadSplatActor -> SplatLoaderService
    // We don't need to trigger it here, or we get double loading loops.
    /*
    useEffect(() => {
        if (!editor || !splatActor) return;

        const subscription = splatActor.subscribe((snapshot) => {
            const node = snapshot.context.node;
            
            if (snapshot.matches('loading') && node?.asset_ref?.url) {
                console.log('[Bridge] Loading Splat:', node.asset_ref.url);
                
                editor.events.invoke('scene.loadUrl', node.asset_ref.url)
                    .catch((err: any) => {
                        console.error('Failed to invoke loadUrl:', err);
                        // Error handling is now done via scene.loadError event
                    });
            }
        });

        return () => subscription.unsubscribe();
    }, [editor, splatActor]);
    */

    // Sync: Camera Config (Initial only)
    useEffect(() => {
        if (!editor || !config?.camera) return;

        // 2. Set Camera
        if (config.camera) {
            const { position, fov, isOrthographic } = config.camera;
            
            // Assuming editor.camera exposes these methods
            // If SuperSplat API is different, we adjust here
            if (editor.scene && editor.scene.camera) {
                // TODO: Check exact API of Camera class in SuperSplat
                // editor.scene.camera.setPosition(position[0], position[1], position[2]);
                // editor.scene.camera.setFov(fov);
            }
        }

    }, [editor, config]);

    // Sync: SuperSplat -> State Machine
    useEffect(() => {
        if (!editor || !splatActor) return;

        const onLoadStart = (filename: string) => {
            console.log('[Bridge] Load Start:', filename);
        };

        const onLoadEnd = (model: any) => {
            console.log('[Bridge] Load End');
            splatActor.send({ type: 'SPLAT.LOADED' });
        };

        const onLoadError = (err: any) => {
            console.error('[Bridge] Load Error:', err);
            splatActor.send({ type: 'SPLAT.ERROR', error: String(err) });
        };

        editor.events.on('scene.loadStart', onLoadStart);
        editor.events.on('scene.loadEnd', onLoadEnd);
        editor.events.on('scene.loadError', onLoadError);

        return () => {
            editor.events.off('scene.loadStart', onLoadStart);
            editor.events.off('scene.loadEnd', onLoadEnd);
            editor.events.off('scene.loadError', onLoadError);
        };
    }, [editor, splatActor]);
}
